import React, { useEffect, useRef, useState } from 'react';
import * as echarts from 'echarts/core';
import { BarChart } from 'echarts/charts';
import { GridComponent, TooltipComponent } from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    TextField,
    Typography,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import SettingsIcon from '@mui/icons-material/Settings';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useNavigate } from 'react-router-dom';
import useAuthorize from '../hooks/useAuthorize';
import { UserRole } from '../types/types';
import { apiDelete, apiDownload, apiGet } from './api';

echarts.use([BarChart, GridComponent, TooltipComponent, CanvasRenderer]);

const money = (value) => Number(value || 0).toLocaleString('en-IN', {
    maximumFractionDigits: 2,
});

const number = (value) => Number(value || 0).toLocaleString('en-IN', {
    maximumFractionDigits: 2,
});

const periodLabels = {
    monthly: 'Monthly',
    quarterly: 'Quarterly',
    yearly: 'Yearly',
};

const SummaryCard = ({ label, value }) => (
    <Card variant="outlined" sx={{ height: '100%' }}>
        <CardContent>
            <Typography variant="body2" color="text.secondary">{label}</Typography>
            <Typography variant="h5" sx={{ mt: 1 }}>{value}</Typography>
        </CardContent>
    </Card>
);

const sortableColumns = [
    { key: 'name', label: 'Name', align: 'left' },
    { key: 'transaction_count', label: 'Rows', align: 'right' },
    { key: 'total_value_usd', label: 'USD', align: 'right' },
    { key: 'total_value_inr', label: 'INR', align: 'right' },
    { key: 'total_quantity', label: 'Qty', align: 'right' },
];

const compareRows = (a, b, key) => {
    if (key === 'name') {
        return String(a.name || '').localeCompare(String(b.name || ''), undefined, {
            numeric: true,
            sensitivity: 'base',
        });
    }
    return Number(a[key] || 0) - Number(b[key] || 0);
};

const sortRows = (rows, sortConfig) => {
    if (!sortConfig?.key) return rows || [];
    const direction = sortConfig.direction === 'desc' ? -1 : 1;
    return (rows || [])
        .slice()
        .sort((a, b) => compareRows(a, b, sortConfig.key) * direction);
};

const MetricBarChart = ({ rows, valueKey = 'total_value_usd', labelKey = 'name', valueLabel = 'USD' }) => {
    const chartRef = useRef(null);

    useEffect(() => {
        if (!chartRef.current) return undefined;
        const chart = echarts.init(chartRef.current);
        const labels = (rows || []).map((row) => row[labelKey] || 'Unmapped');
        const values = (rows || []).map((row) => Number(row[valueKey] || 0));

        chart.setOption({
            grid: {
                left: 56,
                right: 16,
                top: 24,
                bottom: 72,
            },
            tooltip: {
                trigger: 'axis',
                axisPointer: { type: 'shadow' },
                formatter: (params) => {
                    const point = params?.[0];
                    if (!point) return '';
                    return `${point.name}<br/>${valueLabel}: ${money(point.value)}`;
                },
            },
            xAxis: {
                type: 'category',
                data: labels,
                axisLabel: {
                    rotate: labels.length > 4 ? 35 : 0,
                    overflow: 'truncate',
                    width: 90,
                },
            },
            yAxis: {
                type: 'value',
                axisLabel: {
                    formatter: (value) => {
                        if (value >= 10000000) return `${(value / 10000000).toFixed(1)}Cr`;
                        if (value >= 100000) return `${(value / 100000).toFixed(1)}L`;
                        if (value >= 1000) return `${(value / 1000).toFixed(0)}K`;
                        return value;
                    },
                },
                splitLine: {
                    lineStyle: { type: [3, 4], color: '#d6d6d6' },
                },
            },
            series: [
                {
                    type: 'bar',
                    data: values,
                    barMaxWidth: 34,
                    itemStyle: {
                        color: '#1976d2',
                        borderRadius: [3, 3, 0, 0],
                    },
                },
            ],
        });

        const resize = () => chart.resize();
        window.addEventListener('resize', resize);
        return () => {
            window.removeEventListener('resize', resize);
            chart.dispose();
        };
    }, [rows, labelKey, valueKey, valueLabel]);

    if (!rows || rows.length === 0) {
        return (
            <Box sx={{ height: 320, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography color="text.secondary">No chart data</Typography>
            </Box>
        );
    }

    return <Box ref={chartRef} sx={{ width: '100%', height: 320 }} />;
};

const GroupTable = ({ rows, nameHeader = 'Name', sortConfig, onSort }) => (
    <TableContainer component={Paper} variant="outlined" sx={{ height: 320 }}>
        <Table size="small" stickyHeader>
            <TableHead>
                <TableRow>
                    {sortableColumns.map((column) => (
                        <TableCell key={column.key} align={column.align}>
                            <TableSortLabel
                                active={sortConfig?.key === column.key}
                                direction={sortConfig?.key === column.key ? sortConfig.direction : 'asc'}
                                onClick={() => onSort(column.key)}
                            >
                                {column.key === 'name' ? nameHeader : column.label}
                            </TableSortLabel>
                        </TableCell>
                    ))}
                </TableRow>
            </TableHead>
            <TableBody>
                {(rows || []).map((row, index) => (
                    <TableRow key={`${row.id || row.name || index}`}>
                        <TableCell>{row.name || 'Unmapped'}</TableCell>
                        <TableCell align="right">{number(row.transaction_count)}</TableCell>
                        <TableCell align="right">{money(row.total_value_usd)}</TableCell>
                        <TableCell align="right">{money(row.total_value_inr)}</TableCell>
                        <TableCell align="right">{number(row.total_quantity)}</TableCell>
                    </TableRow>
                ))}
                {(!rows || rows.length === 0) && (
                    <TableRow>
                        <TableCell colSpan={5}>No data</TableCell>
                    </TableRow>
                )}
            </TableBody>
        </Table>
    </TableContainer>
);

const AnalyticsSection = ({ title, subtitle, rows, nameHeader }) => {
    const [sortConfig, setSortConfig] = useState(null);
    const sortedRows = sortRows(rows, sortConfig);

    const handleSort = (key) => {
        setSortConfig((current) => {
            if (current?.key === key) {
                return {
                    key,
                    direction: current.direction === 'asc' ? 'desc' : 'asc',
                };
            }
            return { key, direction: 'asc' };
        });
    };

    return (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Box sx={{ mb: 2 }}>
                <Typography variant="h6">{title}</Typography>
                {subtitle && (
                    <Typography variant="body2" color="text.secondary">{subtitle}</Typography>
                )}
            </Box>
            <Grid container spacing={2}>
                <Grid item xs={12} lg={6}>
                    <GroupTable
                        rows={sortedRows}
                        nameHeader={nameHeader}
                        sortConfig={sortConfig}
                        onSort={handleSort}
                    />
                </Grid>
                <Grid item xs={12} lg={6}>
                    <Paper variant="outlined" sx={{ height: 320 }}>
                        <MetricBarChart rows={sortedRows} />
                    </Paper>
                </Grid>
            </Grid>
        </Paper>
    );
};

const normalizePeriodRows = (rows) => (rows || [])
    .slice()
    .reverse()
    .map((row) => ({
        ...row,
        id: `${row.period_start}-${row.trade_direction}`,
        name: `${row.period_label || String(row.period_start || '').slice(0, 10)} ${row.trade_direction}`,
    }));

const ImportDashboard = () => {
    useAuthorize([UserRole.Admin, UserRole.ProductAdmin]);
    const navigate = useNavigate();
    const [filters, setFilters] = useState({
        trade_direction: '',
        from_date: '',
        to_date: '',
        period: 'monthly',
    });
    const [dashboard, setDashboard] = useState(null);
    const [batches, setBatches] = useState([]);
    const [loading, setLoading] = useState(false);
    const [deletingId, setDeletingId] = useState(null);
    const [error, setError] = useState('');

    const fetchData = async () => {
        setLoading(true);
        setError('');
        try {
            const [dashboardResponse, batchResponse] = await Promise.all([
                apiGet('/import/dashboard', filters),
                apiGet('/import/batches', {
                    _start: 0,
                    _end: 10,
                    _sort: 'created_at',
                    _order: 'DESC',
                }),
            ]);
            setDashboard(dashboardResponse.json);
            setBatches(batchResponse.json || []);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleDownload = async (batch) => {
        setError('');
        try {
            await apiDownload(`/import/batches/${batch.id}/download`, batch.original_filename);
        } catch (err) {
            setError(err.message);
        }
    };

    const handleDelete = async (batch) => {
        if (batch.status === 'processing') {
            setError('This batch is still processing. Refresh and try again after it finishes.');
            return;
        }
        const confirmed = window.confirm(
            `Delete "${batch.original_filename}" and all imported rows from this batch? This cannot be undone.`
        );
        if (!confirmed) return;

        setDeletingId(batch.id);
        setError('');
        try {
            const response = await apiDelete(`/import/batches/${batch.id}`);
            const sourceWarning = response.json?.sourceDeleteError
                ? `Batch data was deleted, but the source file could not be deleted: ${response.json.sourceDeleteError}`
                : '';
            await fetchData();
            if (response.json?.sourceDeleteError) {
                setError(sourceWarning);
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setDeletingId(null);
        }
    };

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const summary = dashboard?.summary || {};
    const periodRows = normalizePeriodRows(dashboard?.periods || dashboard?.monthly);

    return (
        <Box sx={{ p: 3 }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} justifyContent="space-between" sx={{ mb: 2 }}>
                <Box>
                    <Typography variant="h6">Import/Export Dashboard</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Trade uploads, dedupe status, and side-by-side table and chart analytics.
                    </Typography>
                </Box>
                <Stack direction="row" spacing={1} alignItems="center">
                    <Button size="small" variant="contained" startIcon={<CloudUploadIcon />} onClick={() => navigate('/import/upload')}>
                        Upload
                    </Button>
                    <Button size="small" variant="outlined" startIcon={<ShowChartIcon />} onClick={() => navigate('/import/trends')}>
                        Trends
                    </Button>
                    <Button size="small" variant="outlined" startIcon={<SettingsIcon />} onClick={() => navigate('/import/masters')}>
                        Masters
                    </Button>
                </Stack>
            </Stack>

            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
                    <FormControl size="small" sx={{ minWidth: 180 }}>
                        <InputLabel>Direction</InputLabel>
                        <Select
                            label="Direction"
                            value={filters.trade_direction}
                            onChange={(event) => setFilters({ ...filters, trade_direction: event.target.value })}
                        >
                            <MenuItem value="">All</MenuItem>
                            <MenuItem value="IMPORT">Import</MenuItem>
                            <MenuItem value="EXPORT">Export</MenuItem>
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 180 }}>
                        <InputLabel>Granularity</InputLabel>
                        <Select
                            label="Granularity"
                            value={filters.period}
                            onChange={(event) => setFilters({ ...filters, period: event.target.value })}
                        >
                            <MenuItem value="monthly">Monthly</MenuItem>
                            <MenuItem value="quarterly">Quarterly</MenuItem>
                            <MenuItem value="yearly">Yearly</MenuItem>
                        </Select>
                    </FormControl>
                    <TextField
                        size="small"
                        type="date"
                        label="From"
                        InputLabelProps={{ shrink: true }}
                        value={filters.from_date}
                        onChange={(event) => setFilters({ ...filters, from_date: event.target.value })}
                    />
                    <TextField
                        size="small"
                        type="date"
                        label="To"
                        InputLabelProps={{ shrink: true }}
                        value={filters.to_date}
                        onChange={(event) => setFilters({ ...filters, to_date: event.target.value })}
                    />
                    <Button variant="outlined" onClick={fetchData} disabled={loading}>
                        Apply
                    </Button>
                </Stack>
            </Paper>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                    gap: 2,
                    mb: 2,
                }}
            >
                <Box>
                    <SummaryCard label="Transactions" value={number(summary.transaction_count)} />
                </Box>
                <Box>
                    <SummaryCard label="Total USD" value={money(summary.total_value_usd)} />
                </Box>
                <Box>
                    <SummaryCard label="Total INR" value={money(summary.total_value_inr)} />
                </Box>
                <Box>
                    <SummaryCard label="Quantity" value={number(summary.total_quantity)} />
                </Box>
                <Box>
                    <SummaryCard label="Avg USD Unit" value={money(summary.avg_unit_price_usd)} />
                </Box>
            </Box>

            <AnalyticsSection
                title={`${periodLabels[filters.period]} Trade`}
                subtitle="Grouped by selected granularity and direction."
                rows={periodRows}
                nameHeader="Period"
            />
            <AnalyticsSection title="Categories" rows={dashboard?.categories} />
            <AnalyticsSection title="Importers" rows={dashboard?.importers} />
            <AnalyticsSection title="Suppliers" rows={dashboard?.suppliers} />
            <AnalyticsSection title="Exporters" rows={dashboard?.exporters} />
            <AnalyticsSection title="Buyers" rows={dashboard?.buyers} />
            <AnalyticsSection title="Indian Ports" rows={dashboard?.indianPorts} />
            <AnalyticsSection title="Shipment / Foreign Ports" rows={dashboard?.shipmentPorts} />
            <AnalyticsSection title="Countries" rows={dashboard?.countries} />
            <AnalyticsSection title="HSN Codes" rows={dashboard?.hsnCodes} />

            <TableContainer component={Paper} variant="outlined">
                <Box sx={{ p: 2, pb: 0 }}>
                    <Typography variant="h6">Recent Uploads</Typography>
                </Box>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>File</TableCell>
                            <TableCell>Direction</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell align="right">Rows</TableCell>
                            <TableCell align="right">Inserted</TableCell>
                            <TableCell align="right">Duplicate</TableCell>
                            <TableCell align="right">Failed</TableCell>
                            <TableCell align="right">Download</TableCell>
                            <TableCell align="right">Delete</TableCell>
                            <TableCell />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {batches.map((batch) => (
                            <TableRow key={batch.id}>
                                <TableCell>{batch.original_filename}</TableCell>
                                <TableCell>{batch.trade_direction}</TableCell>
                                <TableCell>{batch.status}</TableCell>
                                <TableCell align="right">{batch.total_rows}</TableCell>
                                <TableCell align="right">{batch.inserted_rows}</TableCell>
                                <TableCell align="right">{batch.duplicate_rows}</TableCell>
                                <TableCell align="right">{batch.failed_rows}</TableCell>
                                <TableCell align="right">
                                    <Button
                                        size="small"
                                        startIcon={<DownloadIcon />}
                                        onClick={() => handleDownload(batch)}
                                    >
                                        Source Excel
                                    </Button>
                                </TableCell>
                                <TableCell align="right">
                                    <Button
                                        size="small"
                                        color="error"
                                        startIcon={<DeleteIcon />}
                                        disabled={batch.status === 'processing' || deletingId === batch.id}
                                        onClick={() => handleDelete(batch)}
                                    >
                                        Delete
                                    </Button>
                                </TableCell>
                                <TableCell align="right">
                                    <Button
                                        size="small"
                                        startIcon={<VisibilityIcon />}
                                        onClick={() => navigate(`/import/batches/${batch.id}`)}
                                    >
                                        View
                                    </Button>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

export default ImportDashboard;
